import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Live countdown breakdown: turns N seconds into days/hours/minutes/seconds.
const useCountdown = (endsAtIso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endsAtIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAtIso]);
  return useMemo(() => {
    if (!endsAtIso) return { total: 0, d: 0, h: 0, m: 0, s: 0, expired: true };
    const endMs = new Date(endsAtIso).getTime();
    const total = Math.max(0, Math.floor((endMs - now) / 1000));
    return {
      total,
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
      s: total % 60,
      expired: total === 0,
    };
  }, [endsAtIso, now]);
};

/**
 * LifetimeOfferModal — urgent recurring offer popup, Emergent-style.
 *
 * Auto-shows when:
 *   • Beta workspace
 *   • Active countdown (program_ends_at in the future)
 *   • User hasn't seen it OR last saw >= 24h ago
 * Triggered from DashboardLayout via the `show_lifetime_popup` flag on
 * /api/me/beta-status.
 *
 * Also opens when the user clicks the countdown badge in the header.
 *
 * Dismiss → 24h cooldown via POST /api/me/lifetime-offer-popup/dismiss.
 * "LOCK IN LIFETIME" → existing /api/billing/lifetime-checkout (no
 * payment-flow changes — Stripe owns it).
 */
const LifetimeOfferModal = ({ open, onClose, programEndsAt, isManualOpen = false }) => {
  const { api } = useAuth();
  const countdown = useCountdown(programEndsAt);
  const [checkingOut, setCheckingOut] = useState(false);

  const handleDismiss = async () => {
    // Auto-popup dismissal stamps the cooldown. Manual opens (click on
    // the badge) bypass the cooldown stamp — clicking the badge isn't
    // the user "giving up", they may just be browsing the offer.
    if (!isManualOpen) {
      try {
        await api.post('/api/me/lifetime-offer-popup/dismiss');
      } catch { /* non-blocking */ }
    }
    onClose();
  };

  const handleLockIn = async () => {
    setCheckingOut(true);
    try {
      const res = await api.post('/api/billing/lifetime-checkout');
      if (res.data?.checkout_url) {
        // Hand off to Stripe — they own payment processing.
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
        setCheckingOut(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto"
          onClick={handleDismiss}
          data-testid="lifetime-offer-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="relative w-full max-w-xl rounded-2xl overflow-hidden my-auto"
            style={{
              background: 'radial-gradient(140% 140% at 100% 0%, #2a0e16 0%, #160a14 55%, #08070b 100%)',
              border: '1px solid rgba(244,63,94,0.25)',
              boxShadow: '0 0 80px -10px rgba(244,63,94,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Urgent corner glow */}
            <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30 blur-3xl"
                 style={{ background: 'radial-gradient(circle, #f43f5e 0%, transparent 70%)' }} />
            <div aria-hidden className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
                 style={{ background: 'radial-gradient(circle, #fb923c 0%, transparent 70%)' }} />

            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 z-10"
              data-testid="lifetime-offer-modal-close"
            >
              <X size={18} />
            </button>

            <div className="relative px-7 pt-7 pb-5">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/40 mb-4">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-400" />
                </span>
                <span className="text-[10px] font-bold tracking-[0.2em] text-rose-300 uppercase">Last Chance Pricing</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-[1.05] mb-3">
                Lock in <span className="text-amber-300">Lifetime</span> before<br />
                <span className="text-rose-300">Beta ends</span>.
              </h2>
              <p className="text-sm text-white/70 leading-relaxed">
                One-time payment. Lifetime access. <strong className="text-white">No monthly fees, ever — only pay for usage.</strong> When the timer hits zero, the price jumps to $5,000.
              </p>
            </div>

            {/* Live countdown */}
            <div className="relative mx-7 mb-6 rounded-xl border border-rose-500/25 bg-gradient-to-r from-rose-500/[0.08] to-amber-500/[0.04] p-4">
              <p className="text-[9px] uppercase tracking-[0.22em] text-rose-300/80 font-semibold mb-2">Beta ends in</p>
              <div className="flex items-center justify-between gap-2" data-testid="lifetime-offer-countdown">
                {[
                  { v: countdown.d, label: 'days' },
                  { v: countdown.h, label: 'hours' },
                  { v: countdown.m, label: 'mins' },
                  { v: countdown.s, label: 'secs' },
                ].map((seg, i) => (
                  <React.Fragment key={seg.label}>
                    <div className="flex-1 text-center">
                      <div className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none">
                        {String(seg.v).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{seg.label}</div>
                    </div>
                    {i < 3 && <div className="text-2xl text-white/30 -mt-3">:</div>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Price comparison */}
            <div className="relative mx-7 mb-5 rounded-xl bg-white/[0.03] border border-white/[0.06] p-5">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Lifetime today</p>
                  <p className="text-5xl font-bold text-amber-300 tracking-tight tabular-nums">$3,000</p>
                  <p className="text-xs text-white/50 mt-1">One time. No recurring fees.</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">After timer</p>
                  <p className="text-2xl font-semibold text-rose-300/80 line-through decoration-rose-400/60 tabular-nums">$5,000</p>
                  <p className="text-[11px] text-rose-300 font-semibold mt-1">+$2,000 increase</p>
                </div>
              </div>
            </div>

            {/* Whats included — keep it 1-2 lines so the modal stays focused */}
            <div className="relative mx-7 mb-5 grid grid-cols-2 gap-2 text-[12px] text-white/75">
              {[
                'Every current + future feature',
                'Unlimited voice & chat agents',
                'Unlimited sub-accounts & clients',
                'All future upgrades included',
              ].map(line => (
                <div key={line} className="flex items-start gap-1.5">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <div className="relative px-7 pb-6 pt-1 space-y-2.5">
              <button
                onClick={handleLockIn}
                disabled={checkingOut}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f] text-base font-bold shadow-lg shadow-amber-500/20 hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="lifetime-offer-checkout-btn"
              >
                {checkingOut ? (
                  <><RefreshCw size={16} className="animate-spin" /> Opening Stripe…</>
                ) : (
                  <><Zap size={16} fill="#0a0b0f" /> LOCK IN LIFETIME — $3,000</>
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="w-full px-5 py-2.5 rounded-xl text-[12px] text-white/40 hover:text-white/70 transition-colors"
                data-testid="lifetime-offer-dismiss-btn"
              >
                {isManualOpen ? 'Close' : "Maybe later (we'll remind you in 24h)"}
              </button>
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-white/40">
                <AlertTriangle size={10} />
                <span>Secure checkout via Stripe · Price locks the second you complete payment</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LifetimeOfferModal;
export { useCountdown };
